/**
 * Expansion des modules commerciaux agrégés → codes fins ERP.
 * Aligné sur nexxus-licensing-api/src/services/feature-entitlements.ts
 */
export const AGGREGATE_FEATURE_EXPANSION: Record<string, readonly string[]> = {
  rh: ['personnel', 'conges', 'gouvernance'],
  /** Paie seule : sans passerelle compta (paie_comptabilite réservée offre paie+compta / ERP). */
  paie: ['paie', 'cotisations', 'irpp'],
  comptabilite: ['comptabilite', 'analytique', 'cloture', 'rapprochement', 'devises'],
  ecf: ['liasse', 'fiscalite', 'annexes'],
  commercial: ['achats', 'ventes', 'tva'],
  stock: ['stocks'],
  immo: ['immobilisations'],
  /** Alias historiques certificats legacy */
  compta: [
    'comptabilite',
    'analytique',
    'cloture',
    'rapprochement',
    'devises',
    'achats',
    'ventes',
    'tva',
  ],
  etats: ['liasse', 'fiscalite', 'annexes'],
};

export function expandFeatureCodes(codes: string[] | undefined): string[] {
  const expanded = new Set<string>();
  for (const code of codes ?? []) {
    const children = AGGREGATE_FEATURE_EXPANSION[code];
    if (children) {
      for (const child of children) expanded.add(child);
    } else {
      expanded.add(code);
    }
  }
  return [...expanded];
}

/** Modules fins attendus par offre commerciale (après expansion JWT). */
export const EXPECTED_FINE_MODULES_BY_TIER: Record<string, readonly string[]> = {
  paie: expandFeatureCodes(['rh', 'paie']),
  compta: expandFeatureCodes(['comptabilite', 'ecf', 'immo']),
  paie_compta: expandFeatureCodes([
    'rh',
    'paie',
    'paie_comptabilite',
    'comptabilite',
    'ecf',
    'immo',
  ]),
  erp: expandFeatureCodes([
    'rh',
    'paie',
    'comptabilite',
    'ecf',
    'commercial',
    'stock',
    'immo',
    'paie_comptabilite',
    'rgpd',
    'qualite',
  ]),
  starter: expandFeatureCodes(['rh', 'paie']),
  standard: expandFeatureCodes([
    'rh',
    'paie',
    'comptabilite',
    'ecf',
    'commercial',
    'paie_comptabilite',
  ]),
  premium: expandFeatureCodes([
    'rh',
    'paie',
    'comptabilite',
    'ecf',
    'commercial',
    'stock',
    'immo',
    'paie_comptabilite',
    'rgpd',
    'qualite',
  ]),
};

/** Vues interdites pour l’offre Comptabilité seule. */
export const COMPTA_ONLY_FORBIDDEN_VIEWS = [
  'salaries',
  'saisie',
  'bulletins',
  'declarations',
  'conges',
  'coffrefort',
  'attestations',
  'evaluations',
  'recrutement',
] as const;

/** 12 plans commerciaux (4 offres × 3 modes déploiement). */
export const COMMERCIAL_PLAN_CODES = [
  'paie-standalone',
  'paie-hybride',
  'paie-cloud',
  'compta-standalone',
  'compta-hybride',
  'compta-cloud',
  'paie_compta-standalone',
  'paie_compta-hybride',
  'paie_compta-cloud',
  'erp-standalone',
  'erp-hybride',
  'erp-cloud',
] as const;

export type CommercialPlanCode = (typeof COMMERCIAL_PLAN_CODES)[number];

export function tierFromPlanCode(planCode: string): keyof typeof EXPECTED_FINE_MODULES_BY_TIER {
  const tier = planCode.split('-')[0] ?? 'erp';
  if (tier === 'paie' || tier === 'compta' || tier === 'paie_compta' || tier === 'erp') {
    return tier;
  }
  return 'erp';
}

export function dataModeFromPlanCode(planCode: string): 'LOCAL_OFFLINE' | 'REMOTE_MULTI_STATION' {
  return planCode.endsWith('-cloud') ? 'REMOTE_MULTI_STATION' : 'LOCAL_OFFLINE';
}

/** Vues interdites pour l’offre Paie seule. */
export const PAIE_ONLY_FORBIDDEN_VIEWS = [
  'comptabilite',
  'tiers',
  'achats',
  'ventes',
  'stocks',
  'immobilisations',
  'analytique',
  'exercices',
  'tva',
  'etats',
] as const;
